import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import axios from 'axios';
import { Sparkles, RefreshCw } from 'lucide-react';

export default function AIWelcomeMessage() {
  const { API, authHeaders, activeBranch, user } = useAuth();
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchMessage = async (force = false) => {
    if (!activeBranch?.id) return;
    if (force) setRefreshing(true);
    else setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/dashboard/ai-welcome?branch_id=${activeBranch.id}`,
        authHeaders()
      );
      setMessage(data.message || '');
    } catch {
      setMessage('');
    }
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => { fetchMessage(); }, [activeBranch?.id]);

  if (loading) {
    return (
      <div className="relative overflow-hidden rounded-xl border border-amber-500/20 bg-gradient-to-l from-amber-500/5 via-orange-500/5 to-transparent p-5" data-testid="ai-welcome-loading">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-amber-500 animate-pulse" />
          </div>
          <div className="flex-1 space-y-2">
            <div className="h-3 bg-muted rounded-full w-48 animate-pulse" />
            <div className="h-3 bg-muted rounded-full w-72 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!message) return null;

  return (
    <div className="relative overflow-hidden rounded-xl border border-amber-500/20 bg-gradient-to-l from-amber-500/5 via-orange-500/5 to-transparent" data-testid="ai-welcome-message">
      {/* Decorative element */}
      <div className="absolute top-0 end-0 w-32 h-32 bg-gradient-to-bl from-amber-500/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />

      <div className="relative p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20 flex-shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-xs font-bold text-amber-600 dark:text-amber-400" style={{ fontFamily: 'Cairo, sans-serif' }}>
                {isAr ? 'مساعد ذوق الذكي' : 'Zouq AI Assistant'}
              </span>
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 font-semibold">AI</span>
            </div>
            <p className="text-sm text-foreground leading-relaxed whitespace-pre-line" style={{ fontFamily: 'Cairo, Tajawal, sans-serif' }} data-testid="ai-welcome-text">
              {message}
            </p>
          </div>
          <button
            onClick={() => fetchMessage(true)}
            disabled={refreshing}
            className="flex-shrink-0 p-2 rounded-lg hover:bg-muted/60 transition-colors text-muted-foreground hover:text-foreground"
            title={isAr ? 'تحديث' : 'Refresh'}
            data-testid="ai-welcome-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
    </div>
  );
}
